import { useState } from "react";
import Subheadline from "../Elements/Subheadline";
import ProfileCard from "../Cards/CardProfile";
import BtnSecondaryM from "../Buttons/BtnSecondaryM";
import Popup from "../Elements/Popup";
import InputText from "../Inputs/InputText";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  updatePassword,
  createPortalSession,
  cancelSubscription,
} from "../../api";
import { useAuthContext } from "../../context/context";
import { useLanguage } from "../../context/LanguageContext";
import { BASE_URL } from "../../api";
import { useToast } from "../../context/ToastContext";
import PropTypes from "prop-types";

const TabSubscription = ({ navigate, calculateDaysLeft }) => {
  const { userData, setUserData } = useAuthContext();
  const { copy } = useLanguage();
  const { showToast } = useToast();
  const [popupType, setPopupType] = useState(null);

  const planTitle = userData.hasActiveSubscription
    ? "Premium subscription"
    : userData.isTrialActive
      ? "Premium subscription trial"
      : copy.profile.cards.current_plan.title;

  const isSubscriptionCanceled =
    userData?.subscriptions?.[0]?.cancelAtPeriodEnd;

  const handleManageSubscription = async () => {
    try {
      const response = await createPortalSession();
      if (response.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
      } else {
        const errorMsg = response.error || copy.profile.errors.subscription_portal_failed;
        console.error("Portal session error:", errorMsg);
        showToast(errorMsg, "error");
      }
    } catch (error) {
      console.error("Portal session error:", error);
      const errorMsg = error.message || copy.profile.errors.subscription_portal_failed;
      showToast(errorMsg, "error");
    }
  };

  // Check if user has a password
  const hasPassword = !!userData?.password;

  // Password form
  const passwordSchema = hasPassword
    ? z
        .object({
          password: z
            .string()
            .min(12, copy.profile.errors.current_password_required),
          new_password: z
            .string()
            .min(12, copy.profile.errors.new_password_required),
          confirm_password: z
            .string()
            .min(12, copy.profile.errors.confirm_password_required),
        })
        .refine((data) => data.new_password === data.confirm_password, {
          message: copy.register.errors.passwords_do_not_match,
          path: ["confirm_password"],
        })
    : z
        .object({
          new_password: z
            .string()
            .min(12, copy.profile.errors.new_password_required),
          confirm_password: z
            .string()
            .min(12, copy.profile.errors.confirm_password_required),
        })
        .refine((data) => data.new_password === data.confirm_password, {
          message: copy.register.errors.passwords_do_not_match,
          path: ["confirm_password"],
        });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const handleUpdatePassword = async (data) => {
    const userId = userData?.id;
    if (!userId) {
      showToast(copy.profile.errors.user_id_missing, "error");
      return;
    }
    try {
      const response = await updatePassword(userId, data);

      // If password was added for the first time, refresh user data
      if (response.passwordAdded) {
        // Refresh user data to get the updated password field
        const authResponse = await fetch(`${BASE_URL}/getuser`, {
          credentials: "include",
        });
        if (authResponse.ok) {
          const authData = await authResponse.json();
          setUserData(authData.data);
        }
      }

      showToast(copy.profile.errors.password_updated, "success");
      setPopupType(null);
      reset();
    } catch {
      showToast(copy.profile.errors.password_update_failed, "error");
    }
  };

  return (
    <>
      <Subheadline
        subheadline={copy.profile.tab_2}
        description={copy.profile.tab_2_description}
      />

      <div className="flex flex-col gap-3">
        <ProfileCard title={planTitle}>
          <div className="flex flex-col gap-4">
            {userData.hasActiveSubscription ? (
              <>
                <div className="flex flex-col gap-2">
                  <p className="md:text-sm text-base">
                    {isSubscriptionCanceled
                      ? copy.profile.subscription.current_plan.active_until_canceled.replace(
                          "{date}",
                          new Date(
                            new Date(userData.subscriptionEndDate) * 1000
                          ).toLocaleDateString()
                        )
                      : copy.profile.subscription.current_plan.active_until.replace(
                          "{date}",
                          new Date(
                            new Date(userData.subscriptionEndDate) * 1000
                          ).toLocaleDateString()
                        )}
                  </p>


                </div>
                <div className="flex gap-4">
                  <BtnSecondaryM
                    buttonCTA={copy.profile.form.button_manage_subscription}
                    onClickLink={handleManageSubscription}
                  />
                </div>
              </>
            ) : userData.isTrialActive ? (
              <>
                <p className="md:text-sm text-base">
                  {copy.profile.subscription.current_plan.trial_active}  {copy.profile.subscription.current_plan.trial_ends.replace(
                    "{days}",
                    calculateDaysLeft(userData.trialEndDate)
                  )}
                </p>
                <div>
                  <BtnSecondaryM
                    buttonCTA={copy.profile.form.button_upgrade_to_premium}
                    onClickLink={() => navigate("/pricing")}
                  />
                </div>
                
              </>
            ) : (
              <>
                <p className="text-muted">
                  {copy.profile.subscription.current_plan.trial_expired}
                </p>
                <p className="text-sm text-muted">
                  {copy.profile.subscription.current_plan.subscribe_message}
                </p>
                <BtnSecondaryM
                  buttonCTA={copy.profile.form.button_subscribe_now}
                  onClickLink={() => navigate("/pricing")}
                />
              </>
            )}
          </div>
        </ProfileCard>
        {userData.hasActiveSubscription && (
          <ProfileCard
            title={copy.profile.cards.cancel_subscription.title}
            description={
              isSubscriptionCanceled
                ? copy.profile.cards.cancel_subscription.description_canceled.replace(
                    "{date}",
                    new Date(
                      new Date(userData?.subscriptions?.[0]?.canceledAt) * 1000
                    ).toLocaleDateString()
                  )
                : copy.profile.cards.cancel_subscription.description
            }
          >
            {isSubscriptionCanceled ? (
              <BtnSecondaryM
                buttonCTA={copy.profile.form.button_renew_subscription}
                onClickLink={handleManageSubscription}
              />
            ) : (
              <BtnSecondaryM
                buttonCTA={copy.profile.form.button_cancel_subscription}
                disabled={isSubscriptionCanceled}
                onClickLink={() => setPopupType("cancelSubscription")}
              />
            )}
          </ProfileCard>
        )}
      </div>

      {/* Password Popup */}
      {popupType === "password" && (
        <Popup
          isOpen={true}
          onClose={() => setPopupType(null)}
          title={
            hasPassword
              ? copy.profile.form.title_update_password
              : copy.profile.cards.security.add_password_title
          }
          description={
            hasPassword
              ? copy.profile.form.description_update_password
              : copy.profile.cards.security.add_password_description
          }
          buttonSecondaryCTA={copy.dropdown.cancel}
          onClickSecondaryLink={() => setPopupType(null)}
          buttonPrimaryCTA={
            hasPassword
              ? copy.profile.form.button_update
              : copy.profile.cards.security.add_password_title
          }
          onClickPrimaryLink={handleSubmit(handleUpdatePassword)}
        >
          <form
            className="flex flex-col gap-4 mt-4"
            onSubmit={handleSubmit(handleUpdatePassword)}
          >
            {hasPassword && (
              <InputText
                label={copy.profile.form.label_current_password}
                type="password"
                {...register("password")}
                error={errors.password?.message}
                details={`${watch("password").length}/50 characters`}
              />
            )}
            <InputText
              label={copy.profile.form.label_new_password}
              type="password"
              {...register("new_password")}
              error={errors.new_password?.message}
              details={`${watch("new_password").length}/50 ${copy.profile.form.characters}`}
            />
            <InputText
              label={copy.profile.cards.security.confirm_new_password}
              type="password"
              {...register("confirm_password")}
              error={errors.confirm_password?.message}
              details={`${watch("confirm_password").length}/50 ${copy.profile.form.characters}`}
            />
          </form>
        </Popup>
      )}

      {/* Cancel Subscription Popup */}
      {popupType === "cancelSubscription" && (
        <Popup
          isOpen={true}
          onClose={() => setPopupType(null)}
          title={copy.profile.form.title_cancel_subscription}
          description={copy.profile.form.description_cancel_subscription}
          buttonSecondaryCTA={copy.dropdown.keep_subscription}
          onClickSecondaryLink={() => setPopupType(null)}
          buttonPrimaryCTA={copy.profile.form.button_cancel_subscription}
          onClickPrimaryLink={async () => {
            try {
              const res = await cancelSubscription(userData.stripeCustomerId);
              if ("message" in res) {
                showToast(
                  copy.profile.errors.subscription_cancelled,
                  "success"
                );

                // Refresh user data to see the change without a hard reload
                const authResponse = await fetch(`${BASE_URL}/getuser`, {
                  credentials: "include",
                });
                if (authResponse.ok) {
                  const authData = await authResponse.json();
                  setUserData(authData.data);
                } else {
                  window.location.reload();
                }
              } else if ("error" in res) {
                showToast(
                  copy.profile.errors.subscription_cancel_failed,
                  "error"
                );
              }
              setPopupType(null);
            } catch (error) {
              console.error(error);
              showToast(
                copy.profile.errors.subscription_cancel_failed,
                "error"
              );
            }
          }}
        />
      )}
    </>
  );
};

TabSubscription.propTypes = {
  navigate: PropTypes.func.isRequired,
  calculateDaysLeft: PropTypes.func.isRequired,
};

export default TabSubscription;
